/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl),
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      // The window opens once its content is in, styled, and its image
      // decoded (ADR-0024). Opened first, it showed its header alone and then
      // grew to its full height as the content arrived, and the gallery's
      // stylesheet landed after the content: a visible flicker. Until it
      // opens, the card's button shows the theme's loading spinner. A later
      // click on another card wins over this one.
      this.openRequest = (this.openRequest || 0) + 1;
      const request = this.openRequest;
      opener.classList.add('is-loading');

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl),
        };
      }

      try {
        const response = await this.fetch.promise;
        if (!response.ok) throw new Error(`Quick Add fetch failed (${response.status})`);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        if (request !== this.openRequest) return;
        const quickBuyTemplate = tmpl.content.getElementById('quick-buy-template');
        this.productEl = quickBuyTemplate ? quickBuyTemplate.content.querySelector('.js-product') : null;

        if (!this.productEl) throw new Error('Quick Add: #quick-buy-template missing from product response');

        await this.renderProduct(opener);
        if (request !== this.openRequest) return;
        super.open(opener);
      } catch (error) {
        // Don't leave the drawer half-rendered on a failed/empty response:
        // reset productUrl so the next open re-fetches.
        console.warn(error);
        this.classList.remove('is-loading');
        this.productUrl = null;
      } finally {
        this.fetch = null;
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('is-loading');
      }
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     * @returns {Promise<void>} once the content is in and ready to show.
     */
    async renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      // Check whether to select the first variant.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      this.updateFooter();
      await this.updateContent();

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`).click();
      } else {
        const colorOptionDropdown = this.querySelector(`.custom-select__option[data-value="${opener.dataset.selectedColor}"]`);
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Shows a variant's image in the gallery (<mini-gallery>).
     * @param {object} media - The variant's featured media.
     */
    updateMedia(media) {
      const gallery = this.querySelector('mini-gallery');
      if (gallery && typeof gallery.show === 'function') gallery.show(media);
    }

    /**
     * Loads the stylesheets the product content links, before the content is
     * shown: a stylesheet inserted with it applies after it has painted. They
     * go to the head once (theme.utils.loadStylesheet), and one the page
     * already has is left alone.
     * @param {Element} container - The product content, not yet shown.
     * @returns {Promise<void>}
     */
    loadContentStylesheets(container) {
      const links = [...container.querySelectorAll('link[rel="stylesheet"]')];
      links.forEach((link) => link.remove());
      const present = [...document.querySelectorAll('link[rel="stylesheet"]')].map((link) => link.href);
      return Promise.all(
        links
          .map((link) => new URL(link.getAttribute('href'), document.baseURI).href)
          .filter((href) => !present.includes(href))
          .map((href) => theme.utils.loadStylesheet(href).catch(() => {})),
      ).then(() => {});
    }

    /**
     * Loads the scripts the product content carries. Scripts inserted as HTML
     * do not run, and a collection or search page does not load every script
     * a product block needs (product-inventory.js). One the page already has
     * is left alone.
     * @returns {Promise<void>}
     */
    loadContentScripts() {
      const loaded = [...document.scripts].filter((script) => script.src && !this.contains(script)).map((script) => script.src);
      const pending = [...this.querySelectorAll('.js-product-details script[src]')]
        .filter((script) => !loaded.includes(script.src))
        .map((script) => theme.utils.loadScript(script.src).catch(() => {}));
      return Promise.all(pending).then(() => {});
    }

    /**
     * Waits for what would otherwise change the window once open: the main
     * image decoding, and Shop Pay's instalments line (`shopify-payment-terms`
     * draws itself a few hundred milliseconds after it is inserted, adding a
     * line to the price). Each waits half a second at most, side by side: a
     * product without instalments must not hold the window shut.
     * @returns {Promise<void>}
     */
    contentReady() {
      const capped = (promise) =>
        Promise.race([
          promise,
          new Promise((resolve) => {
            setTimeout(resolve, 500);
          }),
        ]);
      const img = this.content.querySelector('.mini-gallery__main img');
      const terms = this.content.querySelector('shopify-payment-terms');
      const drawn = (element) =>
        new Promise((resolve) => {
          const observer = new ResizeObserver(() => {
            if (element.offsetHeight === 0) return;
            observer.disconnect();
            resolve();
          });
          observer.observe(element);
        });
      return Promise.all([img ? capped(img.decode().catch(() => {})) : null, terms ? capped(drawn(terms)) : null]).then(() => {});
    }

    /**
     * Renders the markup for the drawer content element.
     * @returns {Promise<void>} once its stylesheets, scripts and main image are ready.
     */
    async updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        await this.loadContentStylesheets(productContent);
        this.content.innerHTML = productContent.innerHTML;
      }

      // Laid out from here, though the window is still closed (quick-add.css),
      // so contentReady() can see what draws itself after insertion.
      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');

      if (productContent) {
        await this.loadContentScripts();
        await this.contentReady();
      }
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 256 256" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M42.34,138.34A8,8,0,0,1,40,132.69V40h92.69a8,8,0,0,1,5.65,2.34l99.32,99.32a8,8,0,0,1,0,11.31L153,237.66a8,8,0,0,1-11.31,0Z"/>
                  <circle cx="84" cy="84" r="12" fill="currentColor" stroke="none"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}
